import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { FailureDetailOptions } from '@/api/details';
import { useApiErrorMessage, useFailureDetails } from '@/api/use-api-error';
import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { cn } from '@/design/utils';

/**
 * A refusal, rendered: the sentence it is, and the per-field lines it carries.
 *
 * **One live region per failure.** The container is `role="alert"` and the list items are plain
 * `<li>`s with no role of their own, so a screen reader announces the refusal once rather than once
 * per line — and `getByRole('alert')` stays singular wherever a test already asks for it. The
 * per-control problems on the person form keep their own `role="alert"`, and correctly: those sit
 * beside a control that may be thirty fields away from the banner.
 *
 * **Two shapes, because the application already has two.** {@link FailureAlert} is the bordered
 * panel a screen-level read failure renders as; {@link FailureNote} is the line of red text beside a
 * control or under a dialog. They differ in nothing else.
 */

interface FailureProps extends FailureDetailOptions {
  readonly error: unknown;
  readonly className?: string;
}

/** The sentence and its lines, with no container of its own. */
function FailureBody({ error, ...options }: Readonly<FailureProps>): ReactNode {
  const { t } = useTranslation();
  const describe = useApiErrorMessage();
  const detailsOf = useFailureDetails();
  const { shown, hidden } = detailsOf(error, options);

  return (
    <>
      <p>{describe(error)}</p>
      {shown.length > 0 && (
        <ul className="mt-1 list-disc ps-5" data-testid="failure-details">
          {shown.map((detail) => (
            <li key={detail.path}>
              {detail.namesSubject
                ? detail.message
                : t('errors.detail.subject', { subject: detail.subject, message: detail.message })}
            </li>
          ))}
          {hidden > 0 && <li>{t('errors.detail.more', { count: hidden })}</li>}
        </ul>
      )}
    </>
  );
}

/** The panel shape. `Alert` already carries `role="alert"`, so this adds none. */
export function FailureAlert({
  title,
  className,
  ...rest
}: Readonly<FailureProps & { title?: string; 'data-testid'?: string }>): ReactNode {
  const { error, labels, consumed, limit, ...attributes } = rest;
  return (
    <Alert variant="destructive" className={className} {...attributes}>
      {title !== undefined && <AlertTitle>{title}</AlertTitle>}
      <AlertDescription>
        <FailureBody
          error={error}
          {...(labels === undefined ? {} : { labels })}
          {...(consumed === undefined ? {} : { consumed })}
          {...(limit === undefined ? {} : { limit })}
        />
      </AlertDescription>
    </Alert>
  );
}

/** The inline shape: the element the sites it replaces already had, with the list added. */
export function FailureNote({ className, ...rest }: Readonly<FailureProps>): ReactNode {
  return (
    <div role="alert" className={cn('text-sm text-destructive', className)}>
      <FailureBody {...rest} />
    </div>
  );
}

/**
 * What marks the control a refusal is about, and the sentence under it.
 *
 * **Here rather than beside each form**, because every screen that attaches a refusal to a control
 * needs the same three attributes and the same line of red text, and this module is already in the
 * eagerly-loaded half — so a screen in the administration chunk pays nothing for them.
 *
 * The sentence carries no `role` of its own. A form's one live region is its banner: a screen reader
 * that announced the refusal again for every marked control would read it four times, and somebody
 * ON the control reaches it through `aria-describedby`.
 */
export function problemProps(
  id: string,
  message: string | undefined,
): { readonly 'aria-invalid'?: true; readonly 'aria-describedby'?: string } {
  return message === undefined ? {} : { 'aria-invalid': true, 'aria-describedby': id };
}

/** The sentence under one control, when there is one. */
export function ProblemNote({
  id,
  message,
}: Readonly<{ id: string; message: string | undefined }>): ReactNode {
  return message === undefined ? null : (
    <p id={id} className="text-xs text-destructive">
      {message}
    </p>
  );
}
